import { Link } from 'react-router-dom'
import type { Patient, PatientTreatment, PatientPrescription } from '../../store/patientDetailSlice'

interface OverviewTabProps {
  patient: Patient
  treatments: PatientTreatment[]
  prescriptions: PatientPrescription[]
}

function computeAge(dob: string | null): string {
  if (!dob) return ''
  const dateStr = dob.split('T')[0]
  const birth = new Date(dateStr + 'T00:00:00')
  const now = new Date()
  let years = now.getFullYear() - birth.getFullYear()
  const monthDiff = now.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) {
    years--
  }
  if (years < 1) {
    const months = (now.getFullYear() - birth.getFullYear()) * 12 + now.getMonth() - birth.getMonth()
    return months <= 1 ? '1 month' : `${months} months`
  }
  return years === 1 ? '1 year' : `${years} years`
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return ''
  const normalized = dateStr.split('T')[0]
  const d = new Date(normalized + 'T00:00:00')
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

function getStatusStyle(status: string): string {
  switch (status) {
    case 'Planned':
      return 'bg-[#f0e8d0] text-status-warning'
    case 'In Progress':
      return 'bg-[#dae8f0] text-status-info'
    case 'Active':
      return 'bg-accent-subtle-bg text-status-success'
    default:
      return 'bg-border text-text-muted'
  }
}

export function OverviewTab({ patient, treatments, prescriptions }: OverviewTabProps) {
  const activeTreatments = treatments.filter(
    (t) => t.status === 'Planned' || t.status === 'In Progress'
  )
  const activePrescriptions = prescriptions.filter((p) => p.status === 'Active')

  const age = computeAge(patient.date_of_birth)

  return (
    <div className="flex flex-col gap-4" data-testid="overview-tab">
      <div className="border border-border rounded-[6px] bg-surface" data-testid="patient-info-card">
        <div className="px-4 max-sm:px-3 py-3 border-b border-border-light">
          <h3 className="text-[14px] font-medium text-text-primary">Patient Information</h3>
        </div>
        <div className="px-4 max-sm:px-3 py-3 grid grid-cols-2 max-sm:grid-cols-1 gap-x-6 gap-y-2 text-[13px]">
          <div>
            <span className="text-text-muted">Species:</span>{' '}
            <span className="text-text-primary" data-testid="info-species">{patient.species}</span>
          </div>
          <div>
            <span className="text-text-muted">Breed:</span>{' '}
            <span className="text-text-primary" data-testid="info-breed">{patient.breed || '—'}</span>
          </div>
          <div>
            <span className="text-text-muted">Date of Birth:</span>{' '}
            <span className="text-text-primary" data-testid="info-dob">{patient.date_of_birth ? formatDate(patient.date_of_birth) : '—'}</span>
          </div>
          <div>
            <span className="text-text-muted">Age:</span>{' '}
            <span className="text-text-primary" data-testid="info-age">{age || '—'}</span>
          </div>
          <div>
            <span className="text-text-muted">Weight:</span>{' '}
            <span className="text-text-primary" data-testid="info-weight">
              {patient.weight != null ? `${parseFloat(String(patient.weight))} ${patient.weight_unit}` : '—'}
            </span>
          </div>
          <div>
            <span className="text-text-muted">Sex:</span>{' '}
            <span className="text-text-primary" data-testid="info-sex">{patient.sex}</span>
          </div>
          <div>
            <span className="text-text-muted">Spayed/Neutered:</span>{' '}
            <span className="text-text-primary" data-testid="info-spay-neuter">{patient.spay_neuter_status ? 'Yes' : 'No'}</span>
          </div>
          <div>
            <span className="text-text-muted">Microchip:</span>{' '}
            <span className="text-text-primary" data-testid="info-microchip">{patient.microchip_number || '—'}</span>
          </div>
          {patient.notes && (
            <div className="col-span-2">
              <span className="text-text-muted">Notes:</span>{' '}
              <span className="text-text-primary" data-testid="info-notes">{patient.notes}</span>
            </div>
          )}
        </div>
      </div>

      <div className="border border-border rounded-[6px] bg-surface" data-testid="active-treatments-section">
        <div className="px-4 max-sm:px-3 py-3 border-b border-border-light">
          <h3 className="text-[14px] font-medium text-text-primary">Active Treatment Plans</h3>
        </div>
        <div className="px-4 py-2">
          {activeTreatments.length === 0 ? (
            <p className="text-[13px] text-text-muted py-2" data-testid="no-active-treatments">
              No active treatment plans
            </p>
          ) : (
            <div className="flex flex-col">
              {activeTreatments.map((t) => {
                const txId = `TX-${String(t.treatment_number).padStart(4, '0')}`
                return (
                  <Link
                    key={t.id}
                    to={`/treatments/${t.id}`}
                    data-testid="active-treatment-item"
                    className="flex items-center justify-between py-2 hover:bg-hover rounded-[4px] px-1 -mx-1 text-[13px] text-text-primary no-underline"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-text-muted font-[450]">{txId}</span>
                      <span>{t.treatment_type}</span>
                    </div>
                    <span className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${getStatusStyle(t.status)}`}>
                      {t.status}
                    </span>
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      </div>

      <div className="border border-border rounded-[6px] bg-surface" data-testid="current-prescriptions-section">
        <div className="px-4 max-sm:px-3 py-3 border-b border-border-light">
          <h3 className="text-[14px] font-medium text-text-primary">Current Prescriptions</h3>
        </div>
        <div className="px-4 py-2">
          {activePrescriptions.length === 0 ? (
            <p className="text-[13px] text-text-muted py-2" data-testid="no-current-prescriptions">
              No current prescriptions
            </p>
          ) : (
            <div className="flex flex-col">
              {activePrescriptions.map((p) => {
                const rxId = `RX-${String(p.prescription_number).padStart(4, '0')}`
                return (
                  <Link
                    key={p.id}
                    to={`/prescriptions/${p.id}`}
                    data-testid="current-prescription-item"
                    className="flex items-center justify-between py-2 hover:bg-hover rounded-[4px] px-1 -mx-1 text-[13px] text-text-primary no-underline"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-text-muted font-[450]">{rxId}</span>
                      <span>{p.medication_name}</span>
                    </div>
                    <span className="inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium bg-accent-subtle-bg text-status-success">
                      {p.status}
                    </span>
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
